import {css, StyleSheet} from 'aphrodite';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {getRegistrySupportedTypes} from 'constants/RegistryConstants';
import * as SampleConstants from 'constants/SampleConstants';
import {BioentitySummary} from 'folder-items/FileConstants';
import {BenchlingState} from 'redux-stores/reduceApp';
import * as StorableActions from 'samples/StorableActions';
import StorableStore from 'samples/StorableStore';
import {BLUE} from 'ui-library/Colors';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import IndeterminateCheckbox from 'ui-library/IndeterminateCheckbox';
import {MODAL_WIDTH} from 'ui-library/ModalConstants';
import APIUtil from 'util/APIUtil';

interface BasePickContentsFromContainerTableProps {
  containerId: string;
  shouldAllowMultiple?: boolean;
  // If specified, filter to this list of sample types
  sampleTypes?: Array<string>;
  // If specified, filter to this schema
  sampleSchema?: {id: string} | null;
  // An array of contained item IDs that are selected. Pass null for the initial value (all items selected
  // if multiple selections are allowed, otherwise the first item), and once all relevant containers and
  // contained items are loaded, onSelectContainedItems will be called with the specific list of selected
  // items.
  selectedContainedItemIds: Array<string> | null;
  onSelectContainedItems: (items: Array<BioentitySummary | SampleConstants.Batch>) => void;
}

interface PropsFromStorableStore {
  batchesById: {[batchId: string]: SampleConstants.Batch};
  container: SampleConstants.Container | null;
}

interface ConnectedProps {
  containedItems: Array<BioentitySummary | SampleConstants.Batch> | null;
  container: SampleConstants.Container | null;
}

type PickContentsFromContainerTableProps = BasePickContentsFromContainerTableProps &
  PropsFromStorableStore &
  ConnectedProps;

function mapStorableStoreToProps(
  storableStore: typeof StorableStore,
  {containerId}: BasePickContentsFromContainerTableProps
): PropsFromStorableStore {
  return {
    batchesById: storableStore.getBatches(),
    container: storableStore.getContainer(containerId),
  };
}

export function mapStateToProps(
  state: BenchlingState,
  {
    container,
    sampleSchema,
    batchesById,
    sampleTypes,
  }: BasePickContentsFromContainerTableProps & PropsFromStorableStore
): ConnectedProps {
  if (!container) {
    return {
      containedItems: null,
      container: null,
    };
  }
  const {fileSummariesById} = state.models;
  let containedItems: Array<BioentitySummary | SampleConstants.Batch> = [];
  for (const {containedItemId} of container.contents) {
    const bioentity = fileSummariesById[containedItemId];
    const batch = batchesById[containedItemId];
    containedItems.push(bioentity || batch);
  }
  if (containedItems.some((item) => !item)) {
    return {
      containedItems: null,
      container: null,
    };
  }
  if (sampleTypes != null) {
    containedItems = containedItems.filter((item) => sampleTypes.includes(APIUtil.apiIdToType(item.id)));
  }
  if (sampleSchema) {
    containedItems = containedItems.filter(
      (item) =>
        // eslint-disable-next-line benchlint/no-in-operator
        ('tagSchema' in item && item.tagSchema && item.tagSchema.id === sampleSchema.id) ||
        // eslint-disable-next-line benchlint/no-in-operator
        ('batchSchemaId' in item && item.batchSchemaId === sampleSchema.id)
    );
  }
  return {
    containedItems,
    container,
  };
}

export class PickContentsFromContainerTable extends React.Component<PickContentsFromContainerTableProps> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    containerId: PropTypes.string.isRequired,
    shouldAllowMultiple: PropTypes.bool.isRequired,
    // If specified, filter to this list of sample types
    sampleTypes: PropTypes.arrayOf(
      PropTypes.oneOf([ModelTypeConstants.BATCH, ...getRegistrySupportedTypes()])
    ),
    // If specified, filter to this schema
    sampleSchema: PropTypes.object,
    // An array of contained item IDs that are selected. Pass null for the initial value (all items selected
    // if multiple selections are allowed, otherwise the first item), and once all relevant containers and
    // contained items are loaded, onSelectContainedItems will be called with the specific list of selected
    // items.
    selectedContainedItemIds: PropTypes.arrayOf(PropTypes.string.isRequired),
    onSelectContainedItems: PropTypes.func.isRequired,

    // Provided by connect
    containedItems: PropTypes.arrayOf(SampleConstants.CONTAINABLE_SUMMARY_SHAPE),
    container: SampleConstants.CONTAINER_SHAPE,
  };

  static defaultProps = {
    sampleTypes: [ModelTypeConstants.BATCH, ...getRegistrySupportedTypes()],
    shouldAllowMultiple: true,
  };

  componentDidMount(): void {
    if (!this.props.container || !this.props.containedItems) {
      // Note that this loads all contained items for the container as well.
      StorableActions.fetchContainer(this.props.containerId, true);
    }
    this._initializeSelectedContainedItemsIfNecessary();
  }

  componentDidUpdate(): void {
    this._initializeSelectedContainedItemsIfNecessary();
  }

  /**
   * The selectedContainedItemIds prop should normally be an array of contained items, but on initial render
   * the caller isn't able to specify all item IDs because they're not loaded yet. Inform the parent
   * component of the real array of item IDs once we know it, which causes the table to render normally.
   */
  _initializeSelectedContainedItemsIfNecessary(): void {
    if (!this.props.containedItems || this.props.selectedContainedItemIds != null) {
      return;
    }

    if (this.props.shouldAllowMultiple) {
      this.props.onSelectContainedItems(this.props.containedItems);
    } else if (this.props.containedItems.length > 0) {
      this.props.onSelectContainedItems([this.props.containedItems[0]]);
    } else {
      this.props.onSelectContainedItems([]);
    }
  }

  _renderSelectionRowsTable(): JSX.Element {
    const rows = this.props.containedItems!.map((item) => (
      <tr
        className="pickContainerContentsModal-bodyRow"
        key={item.id}
        onClick={() => {
          this._handleClickCheckbox(item.id);
        }}
      >
        <td className="pickContainerContentsModal-checkboxCell">
          <input
            className={css(styles.checkbox)}
            checked={this.props.selectedContainedItemIds!.includes(item.id)}
            onChange={() => {
              this._handleClickCheckbox(item.id);
            }}
            type={this.props.shouldAllowMultiple ? 'checkbox' : 'radio'}
          />
        </td>
        <td>{item.name}</td>
        <td>{this.props.container!.name}</td>
      </tr>
    ));

    return (
      <div className="pickContainerContentsModal-bodyContainer">
        <table>
          {this._renderColgroup()}
          <tbody>{rows}</tbody>
        </table>
      </div>
    );
  }

  _handleClickCheckbox(itemId: string): void {
    if (!this.props.shouldAllowMultiple) {
      this.props.onSelectContainedItems(this.props.containedItems!.filter(({id}) => id === itemId));
      return;
    }

    let newSelectedContainedItemIds: Array<string>;
    if (this.props.selectedContainedItemIds!.includes(itemId)) {
      newSelectedContainedItemIds = this.props.selectedContainedItemIds!.filter((id) => id !== itemId);
    } else {
      newSelectedContainedItemIds = [...this.props.selectedContainedItemIds!, itemId];
    }
    // Reorder to match order of this.props.containedItems.
    newSelectedContainedItemIds = this.props
      .containedItems!.map((item) => item.id)
      .filter((id) => newSelectedContainedItemIds.includes(id));
    this.props.onSelectContainedItems(
      this.props.containedItems!.filter(({id}) => newSelectedContainedItemIds.includes(id))
    );
  }

  _renderHeaderCheckbox(): JSX.Element | null {
    return this.props.shouldAllowMultiple ? (
      <IndeterminateCheckbox
        items={this.props.containedItems!.map((item) => item.id)}
        selectedMap={(itemId) => this.props.selectedContainedItemIds!.includes(itemId)}
        onChange={this._handleClickHeaderCheckbox}
      />
    ) : null;
  }

  _handleClickHeaderCheckbox = (): void => {
    assert(this.props.shouldAllowMultiple!, 'Unexpected header checkbox click');
    if (this.props.selectedContainedItemIds!.length < this.props.containedItems!.length) {
      this.props.onSelectContainedItems(this.props.containedItems!);
    } else {
      this.props.onSelectContainedItems([]);
    }
  };

  _renderColgroup(): JSX.Element {
    // We want to span the entire width with specific column widths.
    return (
      <colgroup>
        <col style={{width: 40}} />
        <col style={{width: 180}} />
        <col style={{width: MODAL_WIDTH - 180 - 40}} />
      </colgroup>
    );
  }

  _isDataLoaded(): boolean {
    return (
      this.props.container != null &&
      this.props.containedItems != null &&
      this.props.selectedContainedItemIds != null
    );
  }

  render(): JSX.Element {
    if (!this._isDataLoaded()) {
      return (
        <div className="u-padding text-center">
          <HorizontalThrobber />
        </div>
      );
    }
    if (this.props.containedItems!.length === 0) {
      return (
        <div className="u-padding legacy-font-proxima-14 u-vertical-center-items u-justify-content-center">
          <BenchlingIcon
            icon="exclamation"
            className="u-small-padding-right pickContainerContentsModal-errorIcon"
          />
          <span>No items were found in this container.</span>
        </div>
      );
    }
    return (
      <span>
        <table>
          {this._renderColgroup()}
          <tbody>
            <tr className="pickContainerContentsModal-headerRow">
              <td className="pickContainerContentsModal-checkboxCell">{this._renderHeaderCheckbox()}</td>
              <td>Item</td>
              <td>Container</td>
            </tr>
          </tbody>
        </table>
        {this._renderSelectionRowsTable()}
      </span>
    );
  }
}

export const styles = StyleSheet.create({
  checkbox: {
    accentColor: BLUE,
  },
});

export default StorableStore.connect(mapStorableStoreToProps)(
  connect(mapStateToProps)(PickContentsFromContainerTable)
);
